/**
 * Personal color palettes. Palette is independent of appearance mode
 * (Light/Dark/System). Token values live in src/index.css under
 * `[data-palette="<id>"]` blocks; Blueprint is the default brand palette.
 */
export const COLOR_PALETTES = [
  { id: 'blueprint', name: 'Blueprint', description: 'Deep blueprint blue on draft gray' },
  { id: 'ocean', name: 'Ocean', description: 'Teal with a restrained warm accent' },
  { id: 'forest', name: 'Forest', description: 'Green with warm neutrals' },
  { id: 'plum', name: 'Plum', description: 'Rich purple with a restrained gold accent' },
  { id: 'terracotta', name: 'Terracotta', description: 'Warm clay with cool slate' },
] as const;

export type ColorPaletteId = typeof COLOR_PALETTES[number]['id'];
export const DEFAULT_PALETTE: ColorPaletteId = 'blueprint';

export function isColorPalette(value: unknown): value is ColorPaletteId {
  return typeof value === 'string' && COLOR_PALETTES.some((p) => p.id === value);
}

/** Invalid, missing or legacy values resolve to Blueprint. */
export function normalizePalette(value: unknown): ColorPaletteId {
  return isColorPalette(value) ? value : DEFAULT_PALETTE;
}

/** Per-account device cache so reload paints the right palette before the DB read. */
export const paletteCacheKey = (userId: string) => `workitect-palette:${userId}`;

export function readCachedPalette(userId: string, storage?: Storage): ColorPaletteId | null {
  try {
    const raw = (storage ?? window.localStorage).getItem(paletteCacheKey(userId));
    return isColorPalette(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeCachedPalette(userId: string, palette: ColorPaletteId, storage?: Storage): void {
  try { (storage ?? window.localStorage).setItem(paletteCacheKey(userId), palette); } catch { /* storage unavailable */ }
}

export function clearPalette(root: HTMLElement = document.documentElement): void {
  root.removeAttribute('data-palette');
}

export function applyPalette(palette: ColorPaletteId, root: HTMLElement = document.documentElement): void {
  root.setAttribute('data-palette', palette);
}

/** Signed-in application surfaces that honor a personal palette. */
export function isPersonalPaletteRoute(pathname: string): boolean {
  return /^\/(app|admin|architect)(\/|$)/.test(pathname);
}
